import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, message } from 'antd';
import { isAxiosError } from 'axios';
import { useSearchParams } from 'react-router-dom';

import type { PosItem } from './api/usePosItemsSearch';
import { PosCartProvider } from './contexts/PosCartContext';
import { type PosShippingAddressEntry } from './components/ShippingMethodCard';
import { ManualDiscountModal } from './components/ManualDiscountModal';
import { CartSummaryBar } from './components/CartSummaryBar';
import { PosCheckoutPanel } from './components/PosCheckoutPanel';
import { PosClosureModal } from './components/PosClosureModal';
import { QuoteSaveModal } from './components/QuoteSaveModal';
import { QuotePostSaveActionsModal } from './components/QuotePostSaveActionsModal';
import { QuoteWarningsPanel } from '@erp/quotes/components/QuoteWarningsPanel';
import { PosWorkspaceHeader } from './components/sections/PosWorkspaceHeader';
import { PosCustomerControl } from './components/sections/PosCustomerControl';
import { PosTopUpSection } from './components/sections/PosTopUpSection';
import { PosCatalogSection } from './components/sections/PosCatalogSection';
import { PosCartSection } from './components/sections/PosCartSection';
import { PosShippingSection } from './components/sections/PosShippingSection';
import { PosFinancialSummary } from './components/sections/PosFinancialSummary';
import { PosPaymentsSection } from './components/sections/PosPaymentsSection';
import { PosPaymentResultSummary } from './components/sections/PosPaymentResultSummary';
import { PosCommissionSummary } from './components/sections/PosCommissionSummary';
import { PosCheckoutActions } from './components/sections/PosCheckoutActions';
// ⬇️ Reemplazo de import: quitamos CustomerSection / CustomerPickerSection y usamos CustomerPicker
import { useCashboxesQuery } from '@erp/payments/api/cashboxes.api';
import { clampCurrency, computeOrderSummary } from './components/discountMath';
import { usePosPermissions } from './hooks/usePosPermissions';
import { usePosKeyboardShortcuts } from './hooks/usePosKeyboardShortcuts';
import { usePosUiState } from './hooks/usePosUiState';
import { usePosPayments } from './hooks/usePosPayments';
import { usePosCartDraft } from './hooks/usePosCartDraft';
import { usePosLayout } from './hooks/usePosLayout';
import { usePosCustomer } from './hooks/usePosCustomer';
import { useShippingOptions } from './hooks/useShippingOptions';
import { useBalanceApplication } from './hooks/useBalanceApplication';
import { adjustCustomerBalance } from './api/customerBalance';
// Importación actualizada: API movida a features/erp/subscriptions
import { subscriptionsApi } from '@features/erp/subscriptions';
import type { PosShortcutDefinition } from './state/types';
import type {
  CommissionOverrideInput,
  ManualDiscountDraft,
  PosLineCommission,
  PosPaymentSummary,
} from './types';
import type { Address } from '@eshop/types/location.types.ts';
import { finalizePosCheckout, resolveErrorMessage } from './utils/checkout';
import { buildIdempotencyKey } from './utils/idempotency';
import { resolveLineKey } from './utils/lineKeys';
import { fetchQuotePosDraft, markQuoteConverted, markQuoteOpenedInPos, updateQuote } from '@erp/quotes/api/quotes.api';
import type { QuoteOut, QuoteWarningOut } from '@erp/quotes/types';
import { buildPosQuoteUpdatePayload } from '@erp/quotes/utils/quotePayload';
import { useQuoteAccess } from '@erp/quotes/hooks/useQuoteAccess';
import { usePromotionsBridge } from './hooks/usePromotionsBridge';
import { useCommissionPreview } from './hooks/useCommissionPreview';
import { deriveEffectiveOrderSummary } from './utils/promotionPricing';
import { getQuickPaymentMethods } from './utils/paymentMethods';

import './PosScreen.css';
import './PosScreen.scss';
import { Customer } from '@store/slices/posCustomersSlice.tsx';
import { getFormattedPrice } from '@tools/utils/common.tsx';
import { useAppSelector } from '@store/store.tsx';
import { selectSiteApps } from '@store/slices/settingsSlice.tsx';

const DEFAULT_COMMISSION_RATE = 0.5;

const describeShortcut = (shortcut: PosShortcutDefinition | undefined): string | null => {
  if (!shortcut) return null;

  const parts: string[] = [];
  if (shortcut.system) {
    parts.push('⌘/Ctrl');
  } else {
    if (shortcut.meta) parts.push('⌘');
    if (shortcut.ctrl) parts.push('Ctrl');
  }
  if (shortcut.shift) parts.push('Shift');
  if (shortcut.alt) parts.push('Alt');

  const normalizedKey = shortcut.key;
  let keyLabel: string;
  if (normalizedKey.length === 1) {
    keyLabel = normalizedKey.toUpperCase();
  } else if (/^f\d+$/i.test(normalizedKey)) {
    keyLabel = normalizedKey.toUpperCase();
  } else {
    keyLabel = normalizedKey.charAt(0).toUpperCase() + normalizedKey.slice(1);
  }
  parts.push(keyLabel);
  return parts.join(' + ');
};

const PosScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const quoteIdParam = searchParams.get('quoteId');
  const quoteId = quoteIdParam ? Number(quoteIdParam) : null;
  const uiState = usePosUiState();
  const {
    getPreferredProfessionalForItem,
    setPreferredProfessionalForItem,
    preferredPaymentMethodId,
    setPreferredPaymentMethodId,
    lastCustomerId,
    setLastCustomerId,
    bottomBarPreferences,
    setBottomBarPreferences,
    isDiscountsPanelCollapsed,
    setDiscountsPanelCollapsed,
    isPaymentsPanelCollapsed,
    setPaymentsPanelCollapsed,
    layoutMode,
    setLayoutMode,
  } = uiState;

  const layout = usePosLayout({
    layoutMode,
    setLayoutMode,
    bottomBarPreferences,
    setBottomBarPreferences,
    isDiscountsPanelCollapsed,
    setDiscountsPanelCollapsed,
    isPaymentsPanelCollapsed,
    setPaymentsPanelCollapsed,
  });

  // Gestión de cliente
  const customerState = usePosCustomer({
    persistCustomerId: setLastCustomerId,
  });
  const {
    customerId,
    customer,
    isHydrating: isCustomerHydrating,
    selectCustomer,
    clearCustomer,
    balance: customerBalance,
    balanceIsLoading,
    refetchBalance: refetchCustomerBalance,
  } = customerState;

  // 🔁 Puente de id → selección (para CustomerPicker)
  const handleCustomerChange = useCallback(
    (id: number | null) => {
      if (id == null) {
        clearCustomer();
        return;
      }
      // selectCustomer históricamente recibía un objeto cliente; pasamos id mínimo
      // para que el hook resuelva/hidrate según su implementación.
      selectCustomer({ id } as Customer);
    },
    [clearCustomer, selectCustomer],
  );

  const siteApps = useAppSelector(selectSiteApps) ?? [];
  const {
    canConvert: canConvertQuotes,
    canSend: canSendQuotes,
    canWrite: canWriteQuotes,
  } = useQuoteAccess();
  const isRrhhEnabled = siteApps.includes('erp__rrhh');
  const offersEnabled = siteApps.includes('erp__sales__offers');
  const subscriptionsEnabled = siteApps.includes('erp__subscriptions');

  // Comisión por defecto
  const computeDefaultCommission = useCallback(
    (item: PosItem, quantity: number, professionalId: number | null): PosLineCommission => {
      if (!isRrhhEnabled) {
        return {
          mode: 'none',
          amount: 0,
          agentId: null,
          agentName: null,
          agreementLabel: null,
          capReached: null,
          message: 'Comisiones deshabilitadas',
          calculatedAmount: 0,
          previousAmount: null,
        };
      }

      const unitValue = item.price?.value ?? 0;
      const baseAmount = unitValue * quantity;
      const professional =
        professionalId != null ? item.professionals?.find((entry) => entry.id === professionalId) : undefined;
      const hasProfessional = item.item_type === 'service' ? professionalId != null : false;
      const fallbackAmount = hasProfessional ? baseAmount * DEFAULT_COMMISSION_RATE : 0;
      const normalizedAmount = Number(fallbackAmount.toFixed(2));
      const agentName = professional?.full_name ?? (hasProfessional ? 'Profesional asignado' : 'Sin asignar');
      const messageLabel = hasProfessional
        ? 'Comisión automática 50% sin acuerdo configurado'
        : item.item_type === 'service'
          ? 'Asigná un profesional para calcular la comisión'
          : 'Sin comisión automática';
      return {
        mode: 'calculated',
        amount: normalizedAmount,
        agentId: hasProfessional ? professionalId : null,
        agentName,
        agreementLabel: hasProfessional ? 'Fallback sin acuerdo' : 'Sin profesional asignado',
        capReached: null,
        message: messageLabel,
        calculatedAmount: normalizedAmount,
        previousAmount: null,
      };
    },
    [isRrhhEnabled],
  );

  // Estado del carrito
  const {
    lines,
    hasLines,
    totalLines,
    orderDiscount,
    addItem: addItemToCart,
    updateQuantity,
    removeLine: removeLineFromCart,
    updateProfessional,
    updateVariant,
    updateNotes,
    updateSubscriptionUsage,
    overrideCommission,
    applyCommissionPreview,
    applyLineDiscount,
    applyOrderDiscount,
    loadDraft,
    resetCart,
  } = usePosCartDraft({
    resolveCommission: computeDefaultCommission,
    getPreferredProfessionalForItem,
    setPreferredProfessionalForItem,
  });

  const commissionPreview = useCommissionPreview(lines, isRrhhEnabled);

  useEffect(() => {
    if (commissionPreview.data) {
      applyCommissionPreview(commissionPreview.data);
    }
  }, [applyCommissionPreview, commissionPreview.data]);

  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [isDiscountModalOpen, setDiscountModalOpen] = useState(false);
  const [discountTargetLineId, setDiscountTargetLineId] = useState<number | null>(null);
  const [discountTargetScope, setDiscountTargetScope] = useState<'line' | 'order'>('order');
  const [isClosureModalOpen, setClosureModalOpen] = useState(false);
  const [lastPaymentSummary, setLastPaymentSummary] = useState<PosPaymentSummary | null>(null);
  const [isProcessingCheckout, setProcessingCheckout] = useState(false);
  const [isQuoteSaveOpen, setQuoteSaveOpen] = useState(false);
  const [savedQuoteForActions, setSavedQuoteForActions] = useState<QuoteOut | null>(null);
  const [isSavingQuoteChanges, setSavingQuoteChanges] = useState(false);
  const [isShippingEnabled, setShippingEnabled] = useState(false);
  const [shippingAddresses, setShippingAddresses] = useState<PosShippingAddressEntry[]>([]);
  const [selectedShippingAddressId, setSelectedShippingAddressId] = useState<string | null>(null);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState<number | null>(null);
  const [shippingManualAmount, setShippingManualAmount] = useState<number | null>(null);
  const [isShippingWizardOpen, setShippingWizardOpen] = useState(false);
  const [isTopUpMode, setTopUpMode] = useState(false);
  const [topUpDirection, setTopUpDirection] = useState<'credit' | 'debit'>('credit');
  const [topUpAmountDraft, setTopUpAmountDraft] = useState('');
  const [topUpMethodId, setTopUpMethodId] = useState<number | null>(null);
  const [topUpNote, setTopUpNote] = useState('');
  const [isProcessingTopUp, setProcessingTopUp] = useState(false);
  const [loadedQuoteId, setLoadedQuoteId] = useState<number | null>(null);
  const [quoteWarnings, setQuoteWarnings] = useState<QuoteWarningOut[]>([]);
  const [isMobileCheckoutOpen, setMobileCheckoutOpen] = useState(false);
  const [isSplitPaymentOpen, setSplitPaymentOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponDraft, setCouponDraft] = useState('');
  const [isCouponModalOpen, setCouponModalOpen] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!quoteId || !Number.isFinite(quoteId) || loadedQuoteId === quoteId) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const quoteDraft = await fetchQuotePosDraft(quoteId);
        if (cancelled) return;
        loadDraft(quoteDraft.draft as Parameters<typeof loadDraft>[0]);
        const draftShipping = (quoteDraft.draft as { shipping?: { methodId?: number; addressId?: string; address?: Address; manualAmount?: number | null } | null }).shipping;
        if (draftShipping?.methodId != null) {
          setShippingEnabled(true);
          setSelectedShippingMethodId(draftShipping.methodId);
          setShippingManualAmount(draftShipping.manualAmount ?? null);
          if (draftShipping.address) {
            const restoredAddressId = draftShipping.addressId ?? `quote-${quoteId}`;
            setShippingAddresses([{ id: restoredAddressId, address: draftShipping.address }]);
            setSelectedShippingAddressId(restoredAddressId);
          } else {
            setSelectedShippingAddressId(draftShipping.addressId ?? null);
          }
        }
        setQuoteWarnings(quoteDraft.warnings ?? []);
        if (quoteDraft.customer_id != null) {
          selectCustomer({ id: quoteDraft.customer_id } as Customer);
        }
        await markQuoteOpenedInPos(quoteId);
        setLoadedQuoteId(quoteId);
        messageApi.success(`Presupuesto #${quoteId} cargado en POS.`);
      } catch (error) {
        if (!cancelled) {
          setQuoteWarnings([]);
          messageApi.error('No se pudo cargar el presupuesto en POS.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadedQuoteId, loadDraft, messageApi, quoteId, selectCustomer]);

  const permissions = usePosPermissions();
  const { canApplyLineDiscount, canApplyOrderDiscount } = permissions;
  const canOverrideCommission = isRrhhEnabled && permissions.canOverrideCommission;
  const canManageDiscounts = canApplyLineDiscount || canApplyOrderDiscount;

  // Fetch cashboxes to check if any require closure
  const cashSessionEnabled = siteApps.includes('erp__cash_session');
  const { data: cashboxes } = useCashboxesQuery({ enabled: cashSessionEnabled });
  const hasBoxesRequiringClosure = useMemo(
    () => cashSessionEnabled && (cashboxes?.some(box => box.requires_closure) ?? false),
    [cashSessionEnabled, cashboxes]
  );

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);
  const paymentSectionRef = useRef<HTMLDivElement | null>(null);
  const shippingAddressCounterRef = useRef(0);

  // Handlers carrito
  const handleAddItem = useCallback(
    (item: PosItem) => {
      if (isTopUpMode) {
        messageApi.warning('Desactivá "Cargar saldo" para agregar ítems a la venta.');
        return;
      }
      const line = addItemToCart(item);
      if (!line) {
        messageApi.warning('Seleccioná una variante antes de agregar el ítem.');
        return;
      }
      setFeedbackMessage(`Se agregó ${line.item.name}`);
    },
    [addItemToCart, isTopUpMode, messageApi],
  );

  const handleRemoveLine = useCallback(
    (lineKey: string) => {
      const removed = removeLineFromCart(lineKey);
      if (removed) {
        setFeedbackMessage(`Se quitó ${removed.item.name}`);
        setDiscountTargetLineId((current) => (current === removed.item.id ? null : current));
      }
    },
    [removeLineFromCart],
  );

  const handleCreateShippingAddress = useCallback(
    async (address: Address): Promise<PosShippingAddressEntry> => {
      shippingAddressCounterRef.current += 1;
      const entry: PosShippingAddressEntry = {
        id: `shipping-${Date.now()}-${shippingAddressCounterRef.current}`,
        address,
      };
      setShippingAddresses((current) => [...current, entry]);
      setSelectedShippingAddressId(entry.id);
      setShippingEnabled(true);
      const alias =
        typeof address.street === 'string' && address.street.trim().length > 0
          ? address.street.trim()
          : address.verbose?.city ?? 'dirección';
      messageApi.success(`Dirección "${alias}" agregada`);
      return entry;
    },
    [messageApi],
  );

  const selectedShippingAddress = useMemo(
    () => shippingAddresses.find((entry) => entry.id === selectedShippingAddressId) ?? null,
    [selectedShippingAddressId, shippingAddresses],
  );

  const shippingOptions = useShippingOptions({
    enabled: isShippingEnabled && selectedShippingAddress != null,
    address: selectedShippingAddress?.address ?? null,
  });

  const shippingMethods = shippingOptions.methods;
  const isShippingLoading = shippingOptions.isLoading;
  const shippingError = shippingOptions.error;

  useEffect(() => {
    if (!isShippingEnabled) {
      setSelectedShippingMethodId(null);
      return;
    }
    if (shippingMethods.length === 0) {
      return;
    }
    if (selectedShippingMethodId != null) {
      const exists = shippingMethods.some((method) => method.id === selectedShippingMethodId);
      if (exists) {
        return;
      }
    }
    const fallbackId = shippingOptions.defaultMethodId ?? shippingMethods[0]?.id ?? null;
    setSelectedShippingMethodId(fallbackId);
  }, [
    isShippingEnabled,
    selectedShippingMethodId,
    shippingMethods,
    shippingOptions.defaultMethodId,
  ]);

  const selectedShippingMethod = useMemo(
    () => shippingMethods.find((method) => method.id === selectedShippingMethodId) ?? null,
    [selectedShippingMethodId, shippingMethods],
  );

  const shippingAmount = useMemo(() => {
    if (!isShippingEnabled || !selectedShippingMethod) {
      return 0;
    }
    if (selectedShippingMethod.strategy === 'manual' || selectedShippingMethod.code === 'to_agree') {
      return Number(Number(shippingManualAmount ?? 0).toFixed(2));
    }
    const raw =
      selectedShippingMethod.calculated_cost?.incl_tax ??
      selectedShippingMethod.calculated_cost?.excl_tax;
    if (raw == null || !Number.isFinite(Number(raw))) {
      return 0;
    }
    return Number(Number(raw).toFixed(2));
  }, [isShippingEnabled, selectedShippingMethod, shippingManualAmount]);

  const shippingCurrency = selectedShippingMethod?.calculated_cost?.currency;

  const orderSummary = useMemo(
    () => computeOrderSummary(lines, orderDiscount ?? undefined, { shippingAmount }),
    [lines, orderDiscount, shippingAmount],
  );

  const promotionPreview = usePromotionsBridge({
    lines,
    orderDiscount,
    customerId,
    couponCode,
    currency: orderSummary.currency,
    enabled: offersEnabled,
  });

  const effectiveOrderSummary = useMemo(
    () => deriveEffectiveOrderSummary(orderSummary, lines, promotionPreview.preview),
    [lines, orderSummary, promotionPreview.preview],
  );
  const displayedOrderDiscount =
    promotionPreview.preview && !promotionPreview.preview.manual_global_won
      ? null
      : orderDiscount;
  const promotionPreviewPending =
    offersEnabled && hasLines && (
      promotionPreview.isRefreshing ||
      !promotionPreview.preview ||
      Boolean(promotionPreview.error)
    );

  const quoteShipping = useMemo(
    () => (isShippingEnabled && selectedShippingMethod
      ? {
          methodId: selectedShippingMethod.id,
          methodName: selectedShippingMethod.name,
          amount: shippingAmount,
          currency: shippingCurrency ?? orderSummary.currency,
          addressId: selectedShippingAddressId,
          address: selectedShippingAddress?.address,
          manualAmount: shippingManualAmount,
        }
      : null),
    [isShippingEnabled, orderSummary.currency, selectedShippingAddress, selectedShippingAddressId, selectedShippingMethod, shippingAmount, shippingCurrency, shippingManualAmount],
  );

  const availableCustomerCredit = useMemo(
    () => clampCurrency(Number(customerBalance?.available_credit ?? 0)),
    [customerBalance],
  );

  const balanceApplication = useBalanceApplication({
    total: effectiveOrderSummary.total,
    balance: customerBalance,
  });

  const totalWithDebt = balanceApplication.totalWithDebt;
  const totalAfterBalance = balanceApplication.totalAfterBalance;
  const isMobile = layout.isMobile;



  const handleToggleTopUp = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        if (customerId == null) {
          messageApi.warning('Seleccioná un cliente para ajustar el saldo.');
          return;
        }
        const activateTopUp = () => {
          if (hasLines) resetCart();
          balanceApplication.reset();
          setShippingEnabled(false);
          setShippingAddresses([]);
          setSelectedShippingAddressId(null);
          setSelectedShippingMethodId(null);
          setMobileCheckoutOpen(false);
          setTopUpMode(true);
        };
        if (hasLines) {
          Modal.confirm({
            title: 'Cambiar a carga de saldo',
            content: 'Se quitarán los ítems del carrito para continuar con una operación de saldo.',
            okText: 'Continuar',
            cancelText: 'Cancelar',
            onOk: activateTopUp,
          });
          return;
        }
        activateTopUp();
      }
      setTopUpMode(enabled);
    },
    [
      balanceApplication.reset,
      customerId,
      hasLines,
      messageApi,
      resetCart,
      setSelectedShippingAddressId,
      setSelectedShippingMethodId,
      setShippingAddresses,
      setShippingEnabled,
    ],
  );

  // Pagos (sensibles al saldo del cliente)
  const payments = usePosPayments({
    totalDue: totalWithDebt,
    balance: customerBalance,
    externalCreditAmount: balanceApplication.appliedCredit,
    balanceAdjustment: balanceApplication.appliedAmount,
  });
  const paymentMethodOptions = payments.paymentMethods;
  const splits = payments.splits;

  const topUpAmountValue = useMemo(() => {
    const raw = topUpAmountDraft.trim();
    if (raw === '') {
      return 0;
    }
    const normalized = Number(raw.replace(',', '.'));
    if (!Number.isFinite(normalized)) {
      return Number.NaN;
    }
    return clampCurrency(Math.abs(normalized));
  }, [topUpAmountDraft]);

  const topUpAmountError = useMemo(() => {
    if (!isTopUpMode && topUpAmountDraft.trim() === '') {
      return null;
    }
    if (topUpAmountDraft.trim() === '') {
      return null;
    }
    if (Number.isNaN(topUpAmountValue)) {
      return 'Ingrese un monto válido.';
    }
    if (topUpAmountValue <= 0) {
      return 'Indicá un monto mayor a cero.';
    }
    if (topUpDirection === 'debit' && topUpAmountValue > availableCustomerCredit + 0.009) {
      return 'No puede superar el saldo disponible.';
    }
    return null;
  }, [
    availableCustomerCredit,
    isTopUpMode,
    topUpAmountDraft,
    topUpAmountValue,
    topUpDirection,
  ]);

  const isTopUpActionDisabled =
    customerId == null ||
    Number.isNaN(topUpAmountValue) ||
    topUpAmountValue <= 0 ||
    topUpMethodId == null ||
    (topUpDirection === 'debit' && topUpAmountValue > availableCustomerCredit + 0.009) ||
    paymentMethodOptions.length === 0 ||
    isProcessingTopUp;

  const topUpButtonLabel = topUpDirection === 'credit' ? 'Cargar saldo' : 'Devolver saldo';

  const emitPaymentFeedback = useCallback(
    (summary: PosPaymentSummary) => {
      if (summary.registerDebt && summary.outstanding > 0) {
        messageApi.warning(
          `La venta quedará con saldo deudor de ${getFormattedPrice(
            summary.outstanding,
            orderSummary.currency,
          )}.`,
        );
      }
      if (summary.changeDue > 0) {
        messageApi.info(
          `Se generará un saldo a favor de ${getFormattedPrice(
            summary.changeDue,
            orderSummary.currency,
          )}.`,
        );
      }
    },
    [messageApi, orderSummary.currency],
  );

  // Persistencia de método de pago preferido
  const updateSplitMethodWithPersistence = useCallback(
    (id: string, methodId: number | null) => {
      payments.updateSplitMethod(id, methodId);
      const primarySplit = payments.splits[0];
      if (primarySplit && primarySplit.id === id) {
        setPreferredPaymentMethodId(methodId);
      }
    },
    [payments, setPreferredPaymentMethodId],
  );

  const quickPaymentMethods = useMemo(
    () => getQuickPaymentMethods(paymentMethodOptions),
    [paymentMethodOptions],
  );

  const handleQuickPayment = useCallback((methodId: number) => {
    const primary = payments.splits[0];
    if (!primary) {
      payments.addSplit();
      return;
    }
    updateSplitMethodWithPersistence(primary.id, methodId);
    payments.fillSplitWithRemaining(primary.id);
    setSplitPaymentOpen(false);
  }, [payments, updateSplitMethodWithPersistence]);

  useEffect(() => {
    if (preferredPaymentMethodId == null) return;
    if (splits.length === 0) return;

    const primarySplit = splits[0];
    if (!primarySplit || primarySplit.methodId === preferredPaymentMethodId) return;

    const exists = paymentMethodOptions.some((m) => m.id === preferredPaymentMethodId);
    if (!exists) return;

    payments.updateSplitMethod(primarySplit.id, preferredPaymentMethodId);
  }, [paymentMethodOptions, payments, preferredPaymentMethodId, splits]);

  useEffect(() => {
    if (preferredPaymentMethodId != null) return;
    if (splits.length === 0) return;

    const primarySplit = splits[0];
    if (primarySplit?.methodId != null) {
      setPreferredPaymentMethodId(primarySplit.methodId);
    }
  }, [preferredPaymentMethodId, setPreferredPaymentMethodId, splits]);

  useEffect(() => {
    if (!isTopUpMode) {
      return;
    }
    if (topUpMethodId != null) {
      return;
    }
    const fallback = preferredPaymentMethodId ?? paymentMethodOptions[0]?.id ?? null;
    if (fallback != null) {
      setTopUpMethodId(fallback);
    }
  }, [
    isTopUpMode,
    paymentMethodOptions,
    preferredPaymentMethodId,
    topUpMethodId,
  ]);

  useEffect(() => {
    if (!isTopUpMode) {
      setTopUpAmountDraft('');
      setTopUpNote('');
      setTopUpMethodId(null);
    }
  }, [isTopUpMode]);

  useEffect(() => {
    if (customerId == null && isTopUpMode) {
      setTopUpMode(false);
      setTopUpAmountDraft('');
      setTopUpNote('');
      setTopUpMethodId(null);
    }
  }, [customerId, isTopUpMode]);

  // Reset de envíos según cliente y líneas
  useEffect(() => {
    if (!hasLines) {
      setShippingEnabled(false);
      setSelectedShippingMethodId(null);
    }
  }, [hasLines]);

  useEffect(() => {
    if (customerId == null) {
      setShippingEnabled(false);
      setShippingAddresses([]);
      setSelectedShippingAddressId(null);
      setSelectedShippingMethodId(null);
      shippingAddressCounterRef.current = 0;
      return;
    }
  }, [customerId]);

  useEffect(() => {
    if (shippingAddresses.length === 0) {
      if (selectedShippingAddressId !== null) {
        setSelectedShippingAddressId(null);
      }
      return;
    }
    const exists = shippingAddresses.some((entry) => entry.id === selectedShippingAddressId);
    if (!exists) {
      setSelectedShippingAddressId(shippingAddresses[0]?.id ?? null);
    }
  }, [selectedShippingAddressId, shippingAddresses]);

  // Mensajes y estados auxiliares
  useEffect(() => {
    if (!feedbackMessage) return;
    const timeout = window.setTimeout(() => setFeedbackMessage(null), 2500);
    return () => window.clearTimeout(timeout);
  }, [feedbackMessage]);

  useEffect(() => {
    if (hasLines) setLastPaymentSummary(null);
  }, [hasLines]);

  // Modal de descuento manual
  const openManualDiscountModal = useCallback((target?: { scope: 'line'; lineId: number } | { scope: 'order' }) => {
    if (!hasLines) {
      return;
    }
    if (!canManageDiscounts) {
      return;
    }
    const fallbackLineId = lines[0]?.item.id ?? null;
    setDiscountTargetScope(target?.scope ?? 'order');
    if (target?.scope === 'line') {
      setDiscountTargetLineId(target.lineId);
    } else {
      setDiscountTargetLineId((current) => (current !== null ? current : fallbackLineId));
    }
    setDiscountModalOpen(true);
  }, [canManageDiscounts, hasLines, lines]);

  // Atajos de teclado
  const focusPaymentsSection = useCallback(() => {
    const section = paymentSectionRef.current;
    if (!section) return;
    const focusable = section.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
    );
    if (focusable) {
      focusable.focus();
      return;
    }
    section.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const keyboardShortcuts = usePosKeyboardShortcuts({
    onFocusSearch: () => searchInputRef.current?.focus(),
    onOpenDiscounts: openManualDiscountModal,
    onOpenPayment: () => {
      if (!isTopUpMode && hasLines) {
        if (isMobile) {
          setMobileCheckoutOpen(true);
          window.setTimeout(focusPaymentsSection, 0);
        } else {
          focusPaymentsSection();
        }
      }
    },
    onConfirm: () => confirmButtonRef.current?.click(),
    isConfirmDisabled: isTopUpMode || !hasLines,
    isCartEmpty: !hasLines,
  });

  const shortcutMap = keyboardShortcuts.shortcuts;

  const paymentShortcutLabel = useMemo(
    () => describeShortcut(shortcutMap.openPayment?.[0]) ?? 'F8',
    [shortcutMap.openPayment],
  );
  const discountShortcutLabel = useMemo(
    () => describeShortcut(shortcutMap.openDiscounts?.[0]) ?? 'F6',
    [shortcutMap.openDiscounts],
  );
  // Resúmenes
  const totalCommission = useMemo(
    () => lines.reduce(
      (acc, line) => acc + (line.commission?.agreementId ? (line.commission.amount ?? 0) : 0),
      0,
    ),
    [lines],
  );
  const commissionCurrency = lines[0]?.item.price?.currency;

  const paymentsWithPreferences = useMemo(
    () => ({
      ...payments,
      updateSplitMethod: updateSplitMethodWithPersistence,
    }),
    [payments, updateSplitMethodWithPersistence],
  );

  /*
  const paymentAggregates = useMemo(() => {
    const summary = payments.summary as PosPaymentSummary | undefined;
    const totals = payments.totals as
      | { outstanding?: number; changeDue?: number }
      | undefined;

    const outstandingSource =
      typeof summary?.outstanding === 'number'
        ? summary.outstanding
        : typeof totals?.outstanding === 'number'
          ? totals.outstanding
          : totalAfterBalance;

    const changeDueSource =
      typeof summary?.changeDue === 'number'
        ? summary.changeDue
        : typeof totals?.changeDue === 'number'
          ? totals.changeDue
          : 0;

    return {
      outstanding: clampCurrency(outstandingSource),
      changeDue: clampCurrency(changeDueSource),
    };
  }, [payments.summary, payments.totals, totalAfterBalance]);
*/

  // Overrides & descuentos
  const handleCommissionOverride = useCallback(
    (itemId: number, override: CommissionOverrideInput) => {
      overrideCommission(itemId, override);
    },
    [overrideCommission],
  );

  const handleApplyLineDiscount = useCallback(
    (lineItemId: number, discount: ManualDiscountDraft | null) => {
      applyLineDiscount(lineItemId, discount);
      setDiscountModalOpen(false);
    },
    [applyLineDiscount],
  );

  const handleApplyOrderDiscount = useCallback(
    (discount: ManualDiscountDraft | null) => {
      applyOrderDiscount(discount);
      setDiscountModalOpen(false);
    },
    [applyOrderDiscount],
  );

  const handleSubmitTopUp = useCallback(async () => {
    if (!isTopUpMode) {
      return;
    }
    if (customerId == null) {
      messageApi.warning('Seleccioná un cliente para ajustar el saldo.');
      return;
    }
    if (topUpMethodId == null) {
      messageApi.warning('Seleccioná un método de pago.');
      return;
    }
    if (Number.isNaN(topUpAmountValue) || topUpAmountValue <= 0) {
      messageApi.warning('Indicá un monto válido mayor a cero.');
      return;
    }
    if (topUpDirection === 'debit' && topUpAmountValue > availableCustomerCredit + 0.009) {
      messageApi.warning('El monto supera el saldo disponible del cliente.');
      return;
    }

    setProcessingTopUp(true);
    try {
      const note = topUpNote.trim();
      const response = await adjustCustomerBalance(customerId, {
        amount: topUpAmountValue,
        direction: topUpDirection,
        payment_method_id: topUpMethodId,
        note: note.length > 0 ? note : null,
        idempotency_key: buildIdempotencyKey('balance-adjust'),
      });
      setPreferredPaymentMethodId(topUpMethodId);
      const currency = response.balance.currency ?? orderSummary.currency;
      const formatted = getFormattedPrice(topUpAmountValue, currency);
      if (topUpDirection === 'credit') {
        messageApi.success(`Se cargaron ${formatted} al saldo del cliente.`);
      } else {
        messageApi.success(`Se devolvieron ${formatted} del saldo del cliente.`);
      }
      setTopUpAmountDraft('');
      setTopUpNote('');
      await refetchCustomerBalance();
      setLastPaymentSummary(null);
    } catch (error) {
      messageApi.error(resolveErrorMessage(error));
    } finally {
      setProcessingTopUp(false);
    }
  }, [
    availableCustomerCredit,
    customerId,
    isTopUpMode,
    messageApi,
    orderSummary.currency,
    refetchCustomerBalance,
    setPreferredPaymentMethodId,
    setTopUpAmountDraft,
    setTopUpNote,
    topUpAmountValue,
    topUpDirection,
    topUpMethodId,
    topUpNote,
  ]);

  const handlePaymentSubmit = useCallback(
    async (summary: PosPaymentSummary) => {
      if (isProcessingCheckout) {
        return;
      }
      if (loadedQuoteId != null && (!canWriteQuotes || !canConvertQuotes)) {
        messageApi.error('No tienes permisos para editar y convertir este presupuesto.');
        return;
      }
      setProcessingCheckout(true);
      try {
        if (loadedQuoteId != null) {
          await updateQuote(
            loadedQuoteId,
            buildPosQuoteUpdatePayload(lines, customer, customerId, orderSummary.currency, orderDiscount, quoteShipping),
          );
        }
        const shippingMethodIdForCheckout =
          isShippingEnabled && selectedShippingMethod ? selectedShippingMethod.id : null;
        const { order, confirmation, saleItemMapping } = await finalizePosCheckout({
          lines,
          customerId,
          orderDiscount,
          couponCode,
          shippingMethodId: shippingMethodIdForCheckout,
          shippingAmountOverride:
            selectedShippingMethod?.strategy === 'manual' || selectedShippingMethod?.code === 'to_agree'
              ? shippingManualAmount
              : null,
          paymentSummary: summary,
          orderSubtotal: orderSummary.subtotal,
          defaultPaymentMethodId: preferredPaymentMethodId ?? paymentMethodOptions[0]?.id ?? null,
          debtOnly: payments.isDebtOnly,
          commissionsEnabled: isRrhhEnabled,
          promotionPreview: promotionPreview.preview,
        });

        const subscriptionLines = lines.filter((line) => line.useSubscription && line.subscriptionId);
        if (subscriptionLines.length > 0) {
          const consumptionResults = await Promise.allSettled(
            subscriptionLines.map((line) => {
              const lineKey = resolveLineKey(line);
              const saleLineId = saleItemMapping[lineKey];
              return subscriptionsApi.consumeCredits(line.subscriptionId as number, {
                item_id: line.item.id,
                quantity: line.quantity,
                source: 'pos',
                metadata_: {
                  sale_order_id: order.id,
                  sale_line_id: saleLineId ?? null,
                },
              });
            }),
          );

          consumptionResults.forEach((result) => {
            if (result.status === 'rejected') {
              console.error('Failed to consume subscription credits', result.reason);
              messageApi.warning('No se pudieron descontar algunos créditos de suscripción');
            }
          });
        }

        const appliedCredit = clampCurrency(Number(confirmation.applied_credit ?? 0));
        const outstanding = clampCurrency(Number(confirmation.outstanding ?? 0));
        const changeDue = clampCurrency(Number(confirmation.change_due ?? 0));
        const updatedSummary: PosPaymentSummary = {
          ...summary,
          creditApplied: appliedCredit,
          balanceApplied:
            summary.balanceApplied >= 0 ? appliedCredit : summary.balanceApplied,
          debtApplied: summary.balanceApplied < 0 ? summary.debtApplied : 0,
          outstanding,
          changeDue,
          registerDebt: outstanding > 0,
        };

        setLastPaymentSummary(updatedSummary);
        emitPaymentFeedback(updatedSummary);
        if (loadedQuoteId != null) {
          try {
            await markQuoteConverted(loadedQuoteId, order.id);
          } catch {
            messageApi.warning('La venta se registro, pero no se pudo marcar el presupuesto como convertido.');
          }
        }
        messageApi.success(`Venta #${order.id} registrada correctamente.`);
        payments.reset();
        balanceApplication.reset();
        resetCart();
        clearCustomer();
        setSplitPaymentOpen(false);
        setCouponCode('');
        setQuoteWarnings([]);
        setShippingEnabled(false);
        setShippingAddresses([]);
        setSelectedShippingAddressId(null);
        setSelectedShippingMethodId(null);
        setShippingManualAmount(null);
        shippingAddressCounterRef.current = 0;
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 409) {
          const detail = error.response.data?.detail;
          if (detail?.code === 'PROMOTION_PREVIEW_STALE') {
            if (detail.preview) {
              promotionPreview.acceptPreview?.(detail.preview);
            }
            messageApi.warning('La oferta cambió. Revisá el resumen y confirmá nuevamente.');
            return;
          }
        }
        messageApi.error(resolveErrorMessage(error));
      } finally {
        setProcessingCheckout(false);
      }
    },
    [
      customer,
      canConvertQuotes,
      canWriteQuotes,
      couponCode,
      customerId,
      clearCustomer,
      emitPaymentFeedback,
      isProcessingCheckout,
      isRrhhEnabled,
      promotionPreview.preview,
      promotionPreview.acceptPreview,
      isShippingEnabled,
      loadedQuoteId,
      lines,
      messageApi,
      orderDiscount,
      orderSummary.currency,
      orderSummary.subtotal,
      payments,
      paymentMethodOptions,
      preferredPaymentMethodId,
      balanceApplication.reset,
      resetCart,
      selectedShippingMethod,
      shippingManualAmount,
      setSelectedShippingAddressId,
      setSelectedShippingMethodId,
      setShippingAddresses,
      setShippingEnabled,
    ],
  );

  const { buildSummary, summary: paymentSummary, isDebtOnly, isLoading: isPaymentsLoading } = payments;

  const hasSplitPayment = splits.length > 1;
  const showDetailedPayment = isSplitPaymentOpen || hasSplitPayment;

  useEffect(() => {
    if (!hasLines) setSplitPaymentOpen(false);
  }, [hasLines]);

  const hasMissingRequiredProfessional = useMemo(() => {
    return lines.some((line) => {
      if (line.item.item_type !== 'service') {
        return false;
      }
      const requiresAppointment = Boolean(line.flags?.requiresAppointment ?? line.item.service_detail?.is_appointment_based);
      if (!requiresAppointment) {
        return false;
      }
      return line.professionalId == null;
    });
  }, [lines]);

  const hasIncompletePaymentSplits = useMemo(() => {
    if (isDebtOnly) {
      return false;
    }
    return paymentSummary.splits.some((split) => split.methodId == null);
  }, [isDebtOnly, paymentSummary]);

  // Permitir confirmar si hay items O si hay ajuste de saldo (cargar/devolver saldo sin items)
  const hasBalanceAdjustment = balanceApplication.appliedAmount !== 0;
  const canProceed = !isTopUpMode && (hasLines || hasBalanceAdjustment);
  const hasBlockingQuoteWarnings = quoteWarnings.some((warning) => warning.severity === 'error');

  const handleSaveQuoteChanges = useCallback(async () => {
    if (!canWriteQuotes || loadedQuoteId == null || isSavingQuoteChanges) return;
    setSavingQuoteChanges(true);
    try {
      const updated = await updateQuote(
        loadedQuoteId,
        buildPosQuoteUpdatePayload(lines, customer, customerId, orderSummary.currency, orderDiscount, quoteShipping),
      );
      messageApi.success(`Presupuesto #${updated.id} actualizado.`);
      setSavedQuoteForActions(updated);
    } catch (error) {
      messageApi.error(resolveErrorMessage(error));
    } finally {
      setSavingQuoteChanges(false);
    }
  }, [
    canWriteQuotes,
    customer,
    customerId,
    isSavingQuoteChanges,
    lines,
    loadedQuoteId,
    messageApi,
    orderDiscount,
    orderSummary.currency,
    quoteShipping,
  ]);

  const handleQuoteSaveAction = useCallback(() => {
    if (!canWriteQuotes) return;
    if (loadedQuoteId != null) {
      void handleSaveQuoteChanges();
      return;
    }
    setQuoteSaveOpen(true);
  }, [canWriteQuotes, handleSaveQuoteChanges, loadedQuoteId]);

  const disableConfirmAction =
    isTopUpMode ||
    !canProceed ||
    isProcessingCheckout ||
    isPaymentsLoading ||
    hasIncompletePaymentSplits ||
    hasMissingRequiredProfessional ||
    hasBlockingQuoteWarnings ||
    promotionPreviewPending ||
    (loadedQuoteId != null && (!canWriteQuotes || !canConvertQuotes));

  const handleConfirmCheckout = useCallback(() => {
    if (isTopUpMode) {
      void handleSubmitTopUp();
      return;
    }
    if (disableConfirmAction) {
      return;
    }
    if (hasMissingRequiredProfessional) {
      messageApi.warning('Asigná un profesional habilitado para cada servicio con turno antes de confirmar.');
      return;
    }
    const summary = buildSummary();
    void handlePaymentSubmit(summary);
  }, [
    buildSummary,
    disableConfirmAction,
    handlePaymentSubmit,
    handleSubmitTopUp,
    hasMissingRequiredProfessional,
    isTopUpMode,
    messageApi,
  ]);

  const handleMobilePrimaryAction = useCallback(() => {
    if (disableConfirmAction) {
      setMobileCheckoutOpen(true);
      window.setTimeout(focusPaymentsSection, 0);
      return;
    }
    handleConfirmCheckout();
  }, [disableConfirmAction, focusPaymentsSection, handleConfirmCheckout]);

  // Updates de líneas
  const handleUpdateQuantity = useCallback(
    (lineKey: string, quantity: number) => {
      updateQuantity(lineKey, quantity);
    },
    [updateQuantity],
  );

  const handleUpdateProfessional = useCallback(
    (lineKey: string, professionalId: number | null) => {
      updateProfessional(lineKey, professionalId);
    },
    [updateProfessional],
  );

  const handleUpdateVariant = useCallback(
    (lineKey: string, variantId: number | null) => {
      updateVariant(lineKey, variantId);
    },
    [updateVariant],
  );

  const handleUpdateNotes = useCallback(
    (lineKey: string, notes: string) => {
      updateNotes(lineKey, notes);
    },
    [updateNotes],
  );

  // Contexto del carrito
  const cartContextValue = useMemo(
    () => ({
      lines,
      addItem: handleAddItem,
      updateQuantity: handleUpdateQuantity,
      removeLine: handleRemoveLine,
      updateProfessional: handleUpdateProfessional,
      updateVariant: handleUpdateVariant,
      updateNotes: handleUpdateNotes,
      updateSubscriptionUsage,
    }),
    [
      handleAddItem,
      handleRemoveLine,
      handleUpdateNotes,
      handleUpdateProfessional,
      handleUpdateQuantity,
      handleUpdateVariant,
      updateSubscriptionUsage,
      lines,
    ],
  );



  const shippingDisabledHint = useMemo(() => {
    if (customerId == null) return 'Asigná un cliente para habilitar envíos.';
    if (!hasLines) return 'Agregá ítems para configurar el envío.';
    return null;
  }, [customerId, hasLines]);

  const shippingCustomerName = useMemo(() => {
    if (!customer) return null;
    const rawName = (customer as { name?: string }).name;
    if (rawName && rawName.trim().length > 0) {
      return rawName.trim();
    }
    const first = (customer as { first_name?: string }).first_name?.trim();
    const last = (customer as { last_name?: string }).last_name?.trim();
    const combined = [first, last].filter(Boolean).join(' ');
    return combined.length > 0 ? combined : null;
  }, [customer]);

  // Verificar si hay items tipo producto (para envío)
  const hasProductItems = useMemo(() => {
    return lines.some(line => line.item.item_type === 'product');
  }, [lines]);

  const customerPickerContent = (
    <PosCustomerControl
      customerId={customerId}
      recentCustomerId={lastCustomerId}
      customer={customer}
      onChange={handleCustomerChange}
      isHydrating={isCustomerHydrating}
      balance={customerBalance}
      balanceIsLoading={balanceIsLoading}
      onSelectCustomer={(nextCustomer) => nextCustomer ? selectCustomer(nextCustomer) : clearCustomer()}
      onClearCustomer={clearCustomer}
    />
  );

  return (
    <PosCartProvider value={cartContextValue}>
      <main
        className={`pos-screen pos-screen__main-container${isMobileCheckoutOpen ? ' pos-screen--checkout-open' : ''}`}
        aria-label="Pantalla principal del POS"
      >
        {contextHolder}
        {loadedQuoteId != null ? (
          <Alert
            type="info"
            showIcon
            title={`Editando presupuesto #${loadedQuoteId}`}
            action={(
              <Button
                size="small"
                type="primary"
                onClick={() => void handleSaveQuoteChanges()}
                disabled={!canWriteQuotes || !hasLines || isSavingQuoteChanges}
              >
                {isSavingQuoteChanges ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            )}
            // description="Los cambios del carrito se guardarán en este presupuesto al confirmar la venta."
            style={{ textAlign: 'start' }}
          />
        ) : null}
        <div className={`pos-screen__grid${isTopUpMode ? ' pos-screen__grid--topup' : ''}`}>
          <section className="pos-screen__stack">
            <PosWorkspaceHeader
              isTopUpMode={isTopUpMode}
              canUseTopUp={customerId != null}
              hasBoxesRequiringClosure={hasBoxesRequiringClosure}
              onOperationChange={(nextIsTopUp) => {
                if (nextIsTopUp) {
                  handleToggleTopUp(true);
                } else {
                  setTopUpMode(false);
                }
              }}
              onOpenClosure={() => setClosureModalOpen(true)}
            />
            {isMobile || isTopUpMode ? customerPickerContent : null}

            {isTopUpMode ? (
              <PosTopUpSection
                availableCustomerCredit={availableCustomerCredit}
                currency={customerBalance?.currency ?? orderSummary.currency}
                topUpDirection={topUpDirection}
                topUpAmountDraft={topUpAmountDraft}
                topUpMethodId={topUpMethodId}
                topUpNote={topUpNote}
                topUpAmountError={topUpAmountError}
                paymentMethodOptions={paymentMethodOptions}
                isTopUpActionDisabled={isTopUpActionDisabled}
                isProcessingTopUp={isProcessingTopUp}
                topUpButtonLabel={topUpButtonLabel}
                onDirectionChange={setTopUpDirection}
                onAmountChange={setTopUpAmountDraft}
                onMethodChange={setTopUpMethodId}
                onNoteChange={setTopUpNote}
                onSubmit={() => void handleSubmitTopUp()}
              />
            ) : (
              <PosCatalogSection
                inputRef={searchInputRef}
                onSelectItem={handleAddItem}
                customerId={customerId}
                subscriptionsEnabled={subscriptionsEnabled}
                cartLines={lines}
                onQuantityChange={handleUpdateQuantity}
                onRemoveLine={handleRemoveLine}
              />
            )}
          </section>

          {!isTopUpMode ? (
            <PosCheckoutPanel
              className="pos-screen__stack pos-screen__checkout-rail"
              aria-label="Paneles secundarios"
              ariaModal={isMobile}
              header={!isMobile ? customerPickerContent : null}
              actions={(
                <PosCheckoutActions
                  canWriteQuotes={canWriteQuotes}
                  hasLines={hasLines}
                  isProcessingCheckout={isProcessingCheckout}
                  isSavingQuoteChanges={isSavingQuoteChanges}
                  loadedQuoteId={loadedQuoteId}
                  disableConfirmAction={disableConfirmAction}
                  total={totalAfterBalance}
                  currency={effectiveOrderSummary.currency}
                  confirmButtonRef={confirmButtonRef}
                  onSaveQuote={handleQuoteSaveAction}
                  onConfirm={handleConfirmCheckout}
                />
              )}
            >
              <PosCartSection
                lines={lines}
                promotionLineAdjustments={effectiveOrderSummary.promotionLineAdjustments}
                totalLines={totalLines}
                feedbackMessage={feedbackMessage}
                canOverrideCommission={canOverrideCommission}
                showCommission={isRrhhEnabled}
                subscriptionsEnabled={subscriptionsEnabled}
                customerId={customerId}
                onQuantityChange={handleUpdateQuantity}
                onRemoveLine={handleRemoveLine}
                onCommissionOverride={handleCommissionOverride}
                onEditDiscount={canApplyLineDiscount ? (lineItemId) => openManualDiscountModal({ scope: 'line', lineId: lineItemId }) : undefined}
                onProfessionalChange={handleUpdateProfessional}
                onNotesChange={handleUpdateNotes}
              />

              <PosShippingSection
                visible={hasProductItems && customerId != null && hasLines}
                open={isShippingWizardOpen}
                enabled={isShippingEnabled}
                shippingDisabledHint={shippingDisabledHint}
                addresses={shippingAddresses}
                selectedAddressId={selectedShippingAddressId}
                methods={shippingMethods}
                methodsLoading={isShippingLoading}
                methodsError={shippingError}
                selectedMethodId={selectedShippingMethodId}
                manualAmount={shippingManualAmount}
                currency={effectiveOrderSummary.currency}
                customerName={shippingCustomerName}
                onClose={() => setShippingWizardOpen(false)}
                onToggle={setShippingEnabled}
                onSelectAddress={setSelectedShippingAddressId}
                onCreateAddress={handleCreateShippingAddress}
                onRetryMethods={shippingOptions.refetch}
                onSelectMethod={setSelectedShippingMethodId}
                onManualAmountChange={setShippingManualAmount}
              />

              <PosFinancialSummary
                subtotal={effectiveOrderSummary.subtotal}
                discounts={effectiveOrderSummary.discounts}
                orderDiscount={displayedOrderDiscount}
                canEditOrderDiscount={canApplyOrderDiscount}
                onEditOrderDiscount={() => openManualDiscountModal({ scope: 'order' })}
                hasLines={hasLines}
                canManageDiscounts={canManageDiscounts}
                discountShortcutLabel={discountShortcutLabel}
                onOpenManualDiscount={openManualDiscountModal}
                promotionPreview={promotionPreview.preview}
                couponCode={couponCode}
                promotionsLoading={promotionPreview.isRefreshing}
                promotionsError={Boolean(promotionPreview.error)}
                onManageCoupon={offersEnabled && hasLines ? () => {
                  setCouponDraft(couponCode);
                  setCouponModalOpen(true);
                } : undefined}
                onRetryPromotions={promotionPreview.refresh}
                shippingEnabled={isShippingEnabled}
                shippingAmount={shippingAmount}
                shippingAvailable={hasProductItems && customerId != null && hasLines}
                shippingLabel={selectedShippingMethod
                  ? `${selectedShippingMethod.name}${selectedShippingAddress ? ` · ${selectedShippingAddress.address.street ?? selectedShippingAddress.address.verbose?.city ?? ''}` : ''}`
                  : 'Envío'}
                onConfigureShipping={() => setShippingWizardOpen(true)}
                appliedBalanceAmount={balanceApplication.appliedAmount}
                total={totalAfterBalance}
                currency={effectiveOrderSummary.currency}
              />

              {/* Envíos: solo mostrar si hay items tipo producto */}
              {/* legacy shipping markup retained only for migration reference
              {false ? (
                isShippingEnabled ? (
                  <section className="pos-panel" aria-label="Opciones de envío">
                    <header className="pos-panel__header">
                      <h2 className="pos-panel__title">Envío</h2>
                      <span className="pos-panel__meta">Opcional</span>
                    </header>
                    <div className="pos-screen__panel-body">
                      <ShippingMethodCard
                        enabled={isShippingEnabled}
                        disabled={Boolean(shippingDisabledHint)}
                        onToggle={setShippingEnabled}
                        addresses={shippingAddresses}
                        selectedAddressId={selectedShippingAddressId}
                        onSelectAddress={setSelectedShippingAddressId}
                        onCreateAddress={handleCreateShippingAddress}
                        methods={shippingMethods}
                        methodsLoading={isShippingLoading}
                        methodsError={shippingError}
                        onRetryMethods={shippingOptions.refetch}
                        selectedMethodId={selectedShippingMethodId}
                        onSelectMethod={setSelectedShippingMethodId}
                        shippingCost={shippingAmount}
                        currency={shippingCurrency ?? orderSummary.currency}
                        customerName={shippingCustomerName}
                      />
                    </div>
                  </section>
                ) : (
                  <section className="pos-panel" aria-label="Agregar envío">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                      }}
                    >
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isShippingEnabled}
                          onChange={(e) => setShippingEnabled(e.target.checked)}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer',
                          }}
                        />
                        <span style={{ fontSize: '0.95rem', fontWeight: 500 }}>
                          Agregar Envío
                        </span>
                      </label>
                    </div>
                  </section>
                )
              ) : null}
              */}

              {/* legacy financial summary retained only for migration reference
              {false ? (<section className="pos-checkout-summary" aria-label="Resumen de la venta">
                <div className="pos-checkout-summary__row">
                  <span>Subtotal</span>
                  <span>{getFormattedPrice(orderSummary.subtotal, orderSummary.currency)}</span>
                </div>
                {orderSummary.discounts > 0.009 ? (
                  <div className="pos-checkout-summary__row pos-checkout-summary__row--discount">
                    <span>Descuentos</span>
                    <span>-{getFormattedPrice(orderSummary.discounts, orderSummary.currency)}</span>
                  </div>
                ) : null}
                {isShippingEnabled ? (
                  <div className="pos-checkout-summary__row">
                    <span>Envío</span>
                    <span>{getFormattedPrice(shippingAmount, orderSummary.currency)}</span>
                  </div>
                ) : null}
                {Math.abs(balanceApplication.appliedAmount) > 0.009 ? (
                  <div className="pos-checkout-summary__row">
                    <span>{balanceApplication.appliedAmount > 0 ? 'Saldo utilizado' : 'Deuda registrada'}</span>
                    <span>{balanceApplication.appliedAmount > 0 ? '-' : '+'}{getFormattedPrice(Math.abs(balanceApplication.appliedAmount), orderSummary.currency)}</span>
                  </div>
                ) : null}
                <div className="pos-checkout-summary__total">
                  <span>Total</span>
                  <strong>{getFormattedPrice(totalAfterBalance, orderSummary.currency)}</strong>
                </div>
              </section>

              </section>) : null}
              */}

              <PosPaymentsSection
                ref={paymentSectionRef}
                paymentShortcutLabel={paymentShortcutLabel}
                showDetailedPayment={showDetailedPayment}
                hasSplitPayment={hasSplitPayment}
                quickPaymentMethods={quickPaymentMethods}
                selectedMethodId={splits[0]?.methodId}
                payments={paymentsWithPreferences}
                totalDue={totalAfterBalance}
                currency={orderSummary.currency}
                hasLines={hasLines}
                isProcessingCheckout={isProcessingCheckout}
                isPaymentsLoading={isPaymentsLoading}
                onQuickPayment={handleQuickPayment}
                onSplitPayment={() => setSplitPaymentOpen(true)}
                onSimplePayment={() => setSplitPaymentOpen(false)}
              />

              {/* legacy payments markup retained only for migration reference
              {false ? (<section className="pos-panel pos-screen__payments-section" aria-label="Métodos de pago">
                <header className="pos-panel__header">
                  <h2 className="pos-panel__title">Pagos</h2>
                  <span className="pos-panel__meta">Atajo {paymentShortcutLabel}</span>
                  {showDetailedPayment ? (
                    <button type="button" className="pos-panel__toggle" onClick={() => setSplitPaymentOpen(false)} disabled={hasSplitPayment}>
                      Pago simple
                    </button>
                  ) : null}
                </header>
                <div className="pos-screen__panel-body" ref={paymentSectionRef}>
                  <QuickPaymentMethods
                    methods={quickPaymentMethods}
                    selectedMethodId={splits[0]?.methodId}
                    onSelect={handleQuickPayment}
                    onSplitPayment={() => setSplitPaymentOpen(true)}
                    disabled={!hasLines || isProcessingCheckout}
                    loading={isPaymentsLoading}
                  />
                  {showDetailedPayment ? (
                    <div className="pos-screen__payment-detail">
                      <PaymentMethodsPanel
                        payments={paymentsWithPreferences}
                        totalDue={totalAfterBalance}
                        currency={orderSummary.currency}
                        showHeader={false}
                        readOnly={!hasLines || isProcessingCheckout}
                        compactRows
                      />
                    </div>
                  ) : null}
                </div>
              </section>

              </section>) : null}
              */}

              {lastPaymentSummary ? <PosPaymentResultSummary summary={lastPaymentSummary} currency={orderSummary.currency} /> : null}

              {isRrhhEnabled && !canOverrideCommission ? (
                <PosCommissionSummary totalCommission={totalCommission} currency={commissionCurrency} />
              ) : null}

            </PosCheckoutPanel>
          ) : null}
        </div>

        <QuoteWarningsPanel warnings={quoteWarnings} />

        {hasMissingRequiredProfessional ? (
          <p className="pos-screen__warning" role="alert">
            Asigná un profesional habilitado para cada servicio con turno antes
            de confirmar la venta.
          </p>
        ) : null}

        {!isTopUpMode ? (
          <CartSummaryBar
            total={totalAfterBalance}
            currency={effectiveOrderSummary.currency}
            subtotal={effectiveOrderSummary.subtotal}
            discountAmount={effectiveOrderSummary.discounts}
            balanceAmount={balanceApplication.appliedAmount}
            balanceDraft={balanceApplication.draft}
            onBalanceChange={balanceApplication.setDraft}
            balanceError={balanceApplication.error}
            balanceAvailable={balanceApplication.creditAvailable}
            balanceDebt={balanceApplication.debtAvailable}
            primaryAction={{
              key: 'confirm',
              label: hasLines
                ? `Cobrar ${getFormattedPrice(totalAfterBalance, effectiveOrderSummary.currency)}`
                : 'Confirmar',
              onClick: handleMobilePrimaryAction,
              disabled: disableConfirmAction,
              buttonRef: confirmButtonRef,
            }}
            secondaryActions={[
              {
                key: 'save-quote',
                label: loadedQuoteId != null ? 'Guardar cambios' : 'Guardar presupuesto',
                onClick: handleQuoteSaveAction,
                disabled: !canWriteQuotes || !hasLines || isTopUpMode || isProcessingCheckout || isSavingQuoteChanges,
                variant: 'secondary',
              },
            ]}
            itemCount={totalLines}
            onOpenCheckout={() => setMobileCheckoutOpen((current) => !current)}
          />
        ) : null}

        {isDiscountModalOpen ? (
          <ManualDiscountModal
            isOpen={isDiscountModalOpen}
            lines={lines}
            initialLineId={discountTargetLineId}
            initialScope={discountTargetScope}
            orderDiscount={
              orderSummary.currency ? orderDiscount : orderDiscount
            }
            onClose={() => setDiscountModalOpen(false)}
            onApplyLine={handleApplyLineDiscount}
            onApplyOrder={handleApplyOrderDiscount}
            canApplyLineDiscount={canApplyLineDiscount}
            canApplyOrderDiscount={canApplyOrderDiscount}
            permissions={permissions}
          />
        ) : null}

        <Modal
          open={isCouponModalOpen}
          title={couponCode ? 'Cambiar cupón' : 'Agregar cupón'}
          okText="Aplicar"
          cancelText="Cancelar"
          onCancel={() => setCouponModalOpen(false)}
          onOk={() => {
            setCouponCode(couponDraft.trim().toUpperCase());
            setCouponModalOpen(false);
          }}
          zIndex={1200}
          footer={(_, { OkBtn, CancelBtn }) => (
            <div className="pos-modal-footer">
              {couponCode ? (
                <Button onClick={() => {
                  setCouponCode('');
                  setCouponDraft('');
                  setCouponModalOpen(false);
                }}>
                  Quitar cupón
                </Button>
              ) : <span />}
              <div><CancelBtn /><OkBtn /></div>
            </div>
          )}
        >
          <label className="pos-coupon-field">
            <span>Código</span>
            <Input
              autoFocus
              value={couponDraft}
              onChange={(event) => setCouponDraft(event.target.value.toUpperCase())}
              onPressEnter={() => {
                setCouponCode(couponDraft.trim().toUpperCase());
                setCouponModalOpen(false);
              }}
              placeholder="Ej. PRIMAVERA10"
              maxLength={64}
              allowClear
            />
          </label>
        </Modal>

        <PosClosureModal
          open={isClosureModalOpen}
          onClose={() => setClosureModalOpen(false)}
        />
        <QuoteSaveModal
          open={isQuoteSaveOpen}
          customerId={customerId}
          customer={customer}
          currencyCode={orderSummary.currency}
          lines={lines}
          orderDiscount={orderDiscount}
          shipping={quoteShipping}
          onClose={() => setQuoteSaveOpen(false)}
          onSaved={(quote) => {
            messageApi.success(`Presupuesto #${quote.id} disponible en ERP.`);
            setSavedQuoteForActions(quote);
          }}
        />
        <QuotePostSaveActionsModal
          quote={savedQuoteForActions}
          canSend={canSendQuotes}
          onClose={() => setSavedQuoteForActions(null)}
        />
      </main>
    </PosCartProvider>
  );
};

export default PosScreen;
